import {
    FC, useEffect, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import Constants, {
    FALLBACK_GENDER, GENDERS,
    DRIVING_LICENSE_REQUIRED_LENGTH, INCOME_RANGES, FALLBACK_INCOME_RANGES
} from '../../../Utils/Constants';
import { IFormStepProps } from '../PageWrapper/PageWrapper';
import DI from '../../../Utils/DI';
import Utils from '../../../Utils/Utils';
import {
    IUserAddressInformation, IUserPersonalInformation, IUserDataItem, IUserAdditionalInformation
} from '../../Components/GeneralInformation/general-information-types';
import useStepContext from '../../Hooks/useStepContext';
import IDataManager, { IReviewUserDetails } from '../../../Business/IDataManager';
import GeneralInformation from '../../Components/GeneralInformation/GeneralInformation';
import IDocumentManager from '../../../Business/IDocumentManager';
import UserService from '../../../Service/UserService';
import { IDropdownItem } from '../../Components/Dropdown/Dropdown';
import { IIdentityDocument } from '../../../Service/IRegistrationService';
import IUserManager from '../../../Business/UserManager/IUserManager';
import { getOccupations } from '../../../Utils/Constants/Occupations';
import { IError } from '../../../Utils/Constants/Errors';
import ResultScreen from '../../Components/ResultScreen/ResultScreen';
import ISessionManager from '../../../Business/ISessionManager';
import { MANDATORY_FIELDS } from '../../../Utils/KYCConfigs';

const env = process.env.REACT_APP_ENV?.toLowerCase();
export interface IOthersData extends IUserPersonalInformation {
    othersNr: IUserDataItem & {validateDocumentFormat: RegExp};
}

const ReviewOtherDetails: FC<IFormStepProps> = ({ nextStepHandler }) => {
    const dataManager: IDataManager = DI.get('DataManager');
    const {
        currentFormStep, whitelistedUser, documentType, setDocumentType, kycDetails
    } = useStepContext();
    const documentManager: IDocumentManager = DI.get('DocumentManager');
    const isMetaDataFrom = currentFormStep.KEY.includes(Constants.UTILS.METADATA);
    const [otherDocumentsRaw, setOtherDocumentsRaw] = useState<IDropdownItem[]>([]);
    const [otherDocuments, setOtherDocuments] = useState<IDropdownItem[]>([]);
    const { t, i18n } = useTranslation();
    const [identityDocuments, setIdentityDocuments] = useState<IDropdownItem[]>([]);
    const [selectedIdentityDocument, setSelectedIdentityDocument] = useState<IDropdownItem>();

    const userManager: IUserManager = dataManager.convertToUserManager();

    const userInfo = userManager.getUserInformation();
    const {
        dateOfBirth,
        firstName,
        lastName,
        gender
    } = userInfo || kycDetails || {};

    const occupations = useMemo(() => getOccupations(i18n.language.toLowerCase()), [i18n.language]);
    const [error, setError] = useState<IError>({
        hasError: false,
        description: '',
        title: ''
    });
    const sessionManager: ISessionManager = DI.get('SessionManager');
    const isAgentLogged = useMemo(() => sessionManager?.getSession()?.getIsValid(), []);

    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                const isFrench = i18n.language.toLowerCase() === 'fr';
                const userService = DI.get('UserService') as UserService;
                const registrationData = userManager.getUserInformation();

                const response = await userService.getIdentityDocuments({
                    relativeUrl: Constants.API_ENDPOINTS.CUSTOMER.IDENTITY_DOCUMENT,
                    data: { standard: 'NON_STANDARD_DOC' }
                });

                const raw = response?.data?.identityDocuments || [];
                setOtherDocumentsRaw(raw);

                const mapped = raw.map((doc: any) => ({
                    id: doc.documentId,
                    key: `othersType.${doc.documentId}`,
                    description: isFrench && doc.fr?.trim() ? doc.fr : doc.en,
                    label: isFrench && doc.fr?.trim() ? doc.fr : doc.en
                }));

                setOtherDocuments(mapped);

                const currentDocType = documentManager.getDocumentType();
                const isOthersDoc = currentDocType === 'OTHERS';

                if (isOthersDoc) {
                    let matchedDropdownItem = mapped.find(
                        (m:any) => m.label?.toLowerCase() === registrationData?.othersType?.toLowerCase()
                    );

                    if (!matchedDropdownItem && documentType) {
                        const matchedRawDoc = raw.find((doc: any) => doc.documentType === documentType);
                        matchedDropdownItem = matchedRawDoc
                            ? mapped.find((m: any) => m.id === matchedRawDoc.documentId)
                            : null;
                    }

                    const selectedItem = matchedDropdownItem
                        ? { ...matchedDropdownItem, label: matchedDropdownItem.description }
                        : Constants.MARKET_GEO_INFORMATION.FALLBACK_OTHERSDOCUMENT;
                    setUserPersonalInformation((prev) => ({
                        ...prev,
                        gender: prev.gender,
                        othersType: isMetaDataFrom
                            ? {
                                type: 'LIST',
                                list: mapped,
                                disabled: false,
                                isValid: selectedItem.description !== 'IDENTITY',
                                selectedItem
                            }
                            : {
                                type: 'TEXT',
                                list: mapped,
                                input: selectedItem.description,
                                disabled: true,
                                isValid: true
                            }
                    }));
                }
            } catch (error:any) {
                const numericCode = Number(error?.errorCode);
                if ([500, 502, 503, 5031, 5032, 504].includes(numericCode)) {
                    setError?.({
                        hasError: true,
                        title: t('error.serviceUnavailable'),
                        description: t('error.serviceUnavailableSubtitle')
                    });
                } else {
                    setError?.({
                        hasError: true,
                        title: t('error.serviceUnavailable'),
                        description: t('error.serviceUnavailableSubtitle')
                    });
                }
            }
        };
        fetchDocuments();
    }, []);
    const identityDocumentsRaw = otherDocumentsRaw as unknown as IIdentityDocument[];

    const matchedIdentityDoc = identityDocumentsRaw.find(
        (doc) => doc.documentType === documentType
    );

    const isFrench = i18n.language.toLowerCase() === 'fr';
    const documentLabel = isFrench
        ? matchedIdentityDoc?.fr
        : matchedIdentityDoc?.en;
    const {
        USER_DETAILS,
        userGender,
        userProvince,
        listOfTowns,
        userTown,
        userOtherDocument,
        userCitizen,
        userOccupation,
        userIncome,
        // userRiskRating,
        userIssuePlaces
    } = useMemo((): IReviewUserDetails => dataManager.convertToReviewUserDetails(
        !currentFormStep?.PROPS?.visited, whitelistedUser, identityDocuments), [currentFormStep, whitelistedUser, identityDocuments]);
    useEffect(() => {
        if (identityDocuments.length > 0 && USER_DETAILS?.getOthersDocument()) {
            const matched = identityDocuments.find(
                (doc) => doc.description === USER_DETAILS.getOthersDocument()
            );
            setSelectedIdentityDocument(matched);
        }
    }, [identityDocuments, USER_DETAILS]);
    const documentNumberField: IUserDataItem & { validateDocumentFormat: RegExp } = (() => {
        const base: Pick<IUserDataItem, 'type' | 'input' | 'acceptanceCriteria'> = {
            type: 'TEXT',
            input: USER_DETAILS.getDocumentNumber(),
            acceptanceCriteria: Utils.withoutSpecialChars
        };

        switch (documentType) {
        case 'PASSPORT':
            return {
                ...base,
                isValid: Utils.validateFieldLength({ value: base.input, min: 9, max: 9 })
                && Utils.withoutSpecialChars(base.input, Constants.DOCUMENT_VALIDATION_RULES.PASSPORT),
                placeHolder: t('generalInformation.passportNr'),
                requiredLength: { min: 9, max: 9 },
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.PASSPORT
            };
        case 'OLD_VOTER_CARD':
        case 'NEW_VOTER_CARD':
            return {
                ...base,
                isValid: Utils.validateFieldLength({ value: base.input, min: 11, max: 11 })
                    && Utils.withoutSpecialChars(base.input, Constants.DOCUMENT_VALIDATION_RULES.VOTER_CARD),
                placeHolder: t('generalInformation.voterNr'),
                requiredLength: { min: 11, max: 11 },
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.VOTER_CARD
            };
        case 'DRIVING_LICENSE':
            return {
                ...base,
                input: USER_DETAILS.getDocumentNumber(),
                isValid: Utils.validateFieldLength({
                    value: USER_DETAILS.getDocumentNumber(),
                    min: DRIVING_LICENSE_REQUIRED_LENGTH.min,
                    max: DRIVING_LICENSE_REQUIRED_LENGTH.max
                }) && Utils.withoutSpecialChars(
                    USER_DETAILS.getDocumentNumber(),
                    Constants.DOCUMENT_VALIDATION_RULES.DRIVING_LICENSE
                ),
                placeHolder: t('generalInformation.drivingLicenseNr'),
                requiredLength: { min: 7, max: 13 },
                acceptanceCriteria: Utils.withoutSpecialChars,
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.DRIVING_LICENSE
            };
        default:
            return {
                ...base,
                isValid: Utils.validateFieldLength({ value: base.input, min: 2, max: 12 })
                    && Utils.withoutSpecialChars(base.input, Constants.DOCUMENT_VALIDATION_RULES.OTHERS),
                placeHolder: t('generalInformation.othersNr'),
                requiredLength: { min: 2, max: 12 },
                validateDocumentFormat: Constants.DOCUMENT_VALIDATION_RULES.OTHERS
            };
        }
    })();
    const documentTypeCode = documentManager.getDocumentType();
    const isOthersDocument = documentTypeCode === 'OTHERS';
    const selectedOthersItem = userOtherDocument
    || otherDocuments.find((doc) => doc.key === documentTypeCode)
    || Constants.MARKET_GEO_INFORMATION.FALLBACK_OTHERSDOCUMENT;
    const readyToRender = useMemo(() => {
        if (!isOthersDocument) return true;

        if (isMetaDataFrom) {
            return otherDocuments.length > 0;
        }

        return !!documentLabel;
    }, [isOthersDocument, isMetaDataFrom, otherDocuments, documentLabel]);
    const othersTypeField = useMemo(() => {
        if (!isOthersDocument || !isMetaDataFrom || !readyToRender) {
            return {
                type: 'TEXT',
                input: documentLabel,
                disabled: true,
                isValid: !!documentLabel
            };
        }
        const selectedItem = userOtherDocument && 'label' in userOtherDocument
            ? { key: userOtherDocument.key, label: userOtherDocument.label }
            : selectedOthersItem && 'label' in selectedOthersItem
                ? { key: selectedOthersItem.key, label: selectedOthersItem.label }
                : undefined;
        return {
            type: 'LIST',
            list: otherDocuments,
            disabled: false,
            isValid: !!selectedItem,
            selectedItem
        };
    }, [
        isOthersDocument,
        isMetaDataFrom,
        readyToRender,
        documentLabel,
        otherDocuments,
        userOtherDocument,
        selectedOthersItem
    ]);
    const formatDate = (dateStr: string) => {
        if (!dateStr || dateStr.length !== 8) return dateStr;
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(4, 6);
        const day = dateStr.slice(6, 8);
        return `${day}/${month}/${year}`;
    };
    const formattedDOB = dateOfBirth ? formatDate(dateOfBirth) : '';
    const dobValue = USER_DETAILS.getDateOfBirth() || formattedDOB;
    const dobValidation = Utils.validateDate(dobValue);

    const initialGender = (() => {
        if (userGender && userGender.key !== FALLBACK_GENDER.key) {
            return userGender;
        }

        const persistedGender = GENDERS.find(
            (g) => g.id === Number(kycDetails?.gender || USER_DETAILS.getGender())
        );

        return persistedGender && persistedGender.key !== FALLBACK_GENDER.key
            ? persistedGender
            : FALLBACK_GENDER;
    })();

    const [userPersonalInformation, setUserPersonalInformation] = useState<IOthersData>({
        firstName: {
            type: 'TEXT',
            input: firstName || USER_DETAILS.getFirstName(),
            isValid: Utils.validateFieldLength(
                { value: firstName || USER_DETAILS.getFirstName(), min: 3, max: 30 }
            ),
            placeHolder: t('generalInformation.firstName'),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars
        },
        lastName: {
            type: 'TEXT',
            input: lastName || USER_DETAILS.getLastName(),
            isValid: Utils.validateFieldLength(
                { value: lastName || USER_DETAILS.getLastName(), min: 3, max: 30 }
            ),
            placeHolder: t('generalInformation.lastName'),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars
        },
        dateOfBirth: {
            type: 'DATE',
            input: formattedDOB || USER_DETAILS.getDateOfBirth(),
            placeHolder: 'DD/MM/YYYY',
            isValid: dobValidation.isValid,
            errorMessage: dobValidation.message,
            requiredLength: { min: 10, max: 10 },
            acceptanceCriteria: Utils.dateCharacters
        },
        gender: {
            type: 'LIST',
            list: GENDERS,
            disabled: false,
            selectedItem: initialGender,
            isValid: initialGender.key !== FALLBACK_GENDER.key,
            isLabelVisible: initialGender.key !== FALLBACK_GENDER.key,
            placeholder: 'GENDER'
        },
        citizen: documentManager.getDocumentType().includes('VOTER') ? {
            type: 'LIST',
            list: Constants.MARKET_GEO_INFORMATION.NATIONALITIES,
            disabled: true,
            isValid: true,
            selectedItem: Constants.MARKET_GEO_INFORMATION.NATIONALITIES
                .find((citizenship) => citizenship.key
                === `citizen.${Constants.MARKET_GEO_INFORMATION.DEFAULT_NATIONALITY_KEY}`)
        } : {
            type: 'LIST',
            list: Constants.MARKET_GEO_INFORMATION.NATIONALITIES,
            disabled: false,
            isValid: !!userCitizen,
            selectedItem:
                userCitizen || Constants.MARKET_GEO_INFORMATION.FALLBACK_NATIONALITY,
            isLabelVisible: !!userCitizen
        },

        ...(isOthersDocument ? { othersType: { ...othersTypeField, isValid: !!othersTypeField.selectedItem } } : {}),

        othersNr: documentNumberField,

        placeOfBirth: {
            type: 'TEXT',
            input: USER_DETAILS.getPlaceOfBirth(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getPlaceOfBirth(), min: 3, max: 30 }
            ),
            placeHolder: t('generalInformation.placeOfBirth'),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars
        }
    });

    const [
        userAddressInformation, setUserAddressInformation
    ] = useState<IUserAddressInformation>({
        street: {
            type: 'TEXT',
            input: USER_DETAILS.getStreet(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getStreet(), min: 3, max: 40 }
            ),
            requiredLength: { min: 3, max: 40 },
            placeHolder: t('generalInformation.street'),
            acceptanceCriteria: Utils.addressStreetCharacters
        },
        province: {
            type: 'LIST',
            isValid: !!userProvince || !!userTown,
            disabled: false,
            selectedItem: userProvince
            || Utils.getProvinceById(userTown?.provinceID)
            || Constants.MARKET_GEO_INFORMATION.FALLBACK_PROVINCE,
            isLabelVisible: !!userProvince || !!userTown,
            list: Constants.MARKET_GEO_INFORMATION.PROVINCES
        },
        town: {
            type: 'LIST',
            isValid: (
                !!userTown
                && (userProvince
                    ? Utils.getTownsByProvinceID(userProvince?.id).includes(userTown)
                    : Utils.getProvinceById(userTown.provinceID)?.id === userTown.provinceID)
            ),
            disabled: false,
            selectedItem: userTown || Constants.MARKET_GEO_INFORMATION.FALLBACK_TOWN,
            isLabelVisible: !!userTown,
            list: userProvince
                ? listOfTowns : Utils.getTownsByProvinceIDFiltered(userTown?.provinceID)
        },
        commune: {
            type: 'TEXT',
            input: USER_DETAILS.getCommune(),
            isValid: Utils.validateFieldLength(
                { value: USER_DETAILS.getCommune(), min: 3, max: 30 }
            ),
            requiredLength: { min: 3, max: 30 },
            acceptanceCriteria: Utils.unicodeChars,
            placeHolder: t('generalInformation.commune')
        }
    });

    const [
        userAdditionalInformation, setUserAdditionalInformation
    ] = useState<IUserAdditionalInformation>({
        occupation: {
            type: 'LIST',
            list: occupations,
            disabled: false,
            isValid: !!userOccupation || !MANDATORY_FIELDS.includes('OCCUPATION'),
            selectedItem: occupations.find(
                (o) => o.value === userOccupation?.value
            ) || Constants.MARKET_GEO_INFORMATION.FALLBACK_OCCUPATION,
            placeHolder: t('generalInformation.occupation')
        },
        income: {
            type: 'LIST',
            list: INCOME_RANGES,
            disabled: false,
            isValid: MANDATORY_FIELDS.includes('INCOME') ? !!userIncome : true,
            selectedItem: userIncome || FALLBACK_INCOME_RANGES,
            placeHolder: t('generalInformation.income')
        },
        idCardIssuingBy: {
            type: 'TEXT',
            input: USER_DETAILS.getidCardIssuingBy() ?? '',
            isValid: MANDATORY_FIELDS.includes('ID_CARD_ISSUING_BY') ? !!USER_DETAILS.getidCardIssuingBy() : true,
            placeHolder: t('generalInformation.idCardIssuingBy'),
            requiredLength: { min: 3, max: 50 },
            acceptanceCriteria: Utils.unicodeChars
        },
        idCardIssuingDate: {
            type: 'DATE',
            input: USER_DETAILS.getidCardIssuingDate() || '',
            placeHolder: 'DD/MM/YYYY',
            isValid: (() => {
                if (!MANDATORY_FIELDS.includes('ID_CARD_ISSUING_DATE')) return true;
                const issuingDate = USER_DETAILS.getidCardIssuingDate();
                if (!issuingDate) return false;
                const result = Utils.validateIdCardIssuingDate(
                    USER_DETAILS.getDateOfBirth(),
                    issuingDate
                );

                return typeof result === 'boolean' ? result : result.isValid;
            })(),
            requiredLength: { min: 10, max: 10 },
            acceptanceCriteria: Utils.dateCharacters
        },
        idCardIssuingPlace: {
            type: 'LIST',
            list: Constants.MARKET_GEO_INFORMATION.idCardIssuingPlace,
            isValid: MANDATORY_FIELDS.includes('ID_CARD_ISSUING_PLACE') ? !!userIssuePlaces : true,
            selectedItem: userIssuePlaces || Constants.MARKET_GEO_INFORMATION.FALLBACK_CARD_ISSUED_PLACE,
            placeHolder: t('generalInformation.idCardIssuingPlace'),
            isLabelVisible: !!userIssuePlaces,
            disabled: false
        }
    });
    return (
        <>
            {!error.hasError && (
                <GeneralInformation
                    nextStepHandler={nextStepHandler}
                    userPersonalInformation={userPersonalInformation}
                    setUserPersonalInformation={setUserPersonalInformation}
                    userAddressInformation={userAddressInformation}
                    userAdditionalInformation={userAdditionalInformation}
                    setUserAdditionalInformation={setUserAdditionalInformation}
                    setUserAddressInformation={setUserAddressInformation}
                    identityDocuments={otherDocumentsRaw}
                />
            )}

            {error.hasError && (
                <ResultScreen
                    title={error.title || t('error.serviceUnavailable')}
                    description={error.description || t('error.serviceUnavailableSubtitle')}
                    actionText={t('general.tryAgain')}
                    actionFn={() => {
                        setError({ hasError: false, title: '', description: '' });
                        nextStepHandler({
                            ...Constants.FORM_STEPS.PERSONAL_DOCUMENTS,
                            TITLE: isAgentLogged ? 'customerDocuments' : 'personalDocuments'
                        });
                    }}
                    showActionButton
                />
            )}
        </>
    );
};

export default ReviewOtherDetails;
